import { Box, CircularProgress, TextField, Typography } from '@mui/material';
import { Field, Form, Formik, FormikHelpers } from 'formik';
import { useRef, useState } from 'react';
import * as Yup from 'yup';
import { ErrorSnackbar } from '../components/ErrorSnackbar';
import { FormHeaderImage, FormHeaderWrapper, FormLoadingOverlay, FormPaper } from '../components/FormStyledComponents';
import { PrivacyConsentSection } from '../components/PrivacyConsentSection';
import { useHeaderImage } from '../hooks/useHeaderImage';
import { LanscoButton } from '../lansco-button';
import { FORM_IDS } from '../utils/formAssets';
import { submitFormToApi } from '../utils/submitForm';

interface FormValues {
  firstName: string;
  lastName: string;
  company: string;
  trainingDate: string;
  email: string;
  consent: boolean;
  // Honeypot: must stay empty for real users; bots tend to fill every field.
  website: string;
}

const today = new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 10);

const validationSchema = Yup.object({
  firstName: Yup.string().required('Vorname ist erforderlich'),
  lastName: Yup.string().required('Nachname ist erforderlich'),
  company: Yup.string().trim().required('Firmenname ist erforderlich'),
  trainingDate: Yup.string()
    .required('Schulungstag ist erforderlich')
    .test('not-future', 'Das Datum darf nicht in der Zukunft liegen', (value) => {
      if (!value) return false;
      return value <= today;
    }),
  email: Yup.string()
    .email('Ungültige E-Mail-Adresse')
    .matches(/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i, 'Ungültiges E-Mail-Format')
    .required('Geschäftsmail ist erforderlich'),
  consent: Yup.boolean()
    .required('Bitte stimme den Datenschutzbestimmungen zu')
    .oneOf([true], 'Du musst den Datenschutzbestimmungen zustimmen, um den Nachweis zu erhalten'),
});

const initialValues: FormValues = {
  firstName: '',
  lastName: '',
  company: '',
  trainingDate: today,
  email: '',
  consent: false,
  website: '',
};

// Same-origin Azure Functions backend (see /api/submit-euaiact)
const API_ENDPOINT = '/api/submit-euaiact';

export const EUAIActZertifikat = () => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showErrorSnackbar, setShowErrorSnackbar] = useState(false);
  const headerImage = useHeaderImage(FORM_IDS.EU_AI_ACT_ZERTIFIKAT);
  // Timestamp when the form was mounted; used for a server-side timing check.
  const formMountedAtRef = useRef(Date.now());

  const handleSubmit = async (
    values: FormValues,
    { setSubmitting }: FormikHelpers<FormValues>
  ) => {
    setIsLoading(true);
    setErrorMessage('');
    setShowErrorSnackbar(false);

    try {
      const result = await submitFormToApi({
        apiEndpoint: API_ENDPOINT,
        payload: {
          firstName: values.firstName,
          lastName: values.lastName,
          company: values.company,
          trainingDate: values.trainingDate,
          mail: values.email,
          consent: values.consent,
          website: values.website,
          elapsedMs: Date.now() - formMountedAtRef.current,
        },
        debugFieldValue: values.firstName,
      });

      if (result.success) {
        setIsSubmitted(true);
      } else {
        setErrorMessage(result.error!);
        setShowErrorSnackbar(true);
      }
    } catch (err) {
      console.error('Fehler beim Absenden des Formulars:', err);
      setErrorMessage('Beim Absenden des Formulars ist ein Fehler aufgetreten. Bitte versuche es erneut.');
      setShowErrorSnackbar(true);
    } finally {
      setIsLoading(false);
      setSubmitting(false);
    }
  };

  return (
    <>
      <FormHeaderWrapper>
        <FormHeaderImage src={headerImage} alt="Lansco Logo" />
      </FormHeaderWrapper>
      <FormPaper elevation={3}>
        {!isSubmitted ? (
          <>
            <Typography variant="h4" component="h1" gutterBottom align="center" sx={{ fontWeight: 300, mb: 1, color: 'primary.main' }}>
              Herzlichen Glückwunsch!
            </Typography>
            <Typography variant="body1" align="center" sx={{ mb: 3 }}>
              Du hast soeben erfolgreich die Basisschulung zum EU AI Act abgeschlossen.
            </Typography>
            <Typography variant="h5" component="h2" gutterBottom align="center" sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
              Teilnehmernachweis
            </Typography>
            <Typography variant="body1" align="center" sx={{ mb: 4 }}>
              Um Deinen persönlichen Teilnehmernachweis zu erhalten, fülle bitte folgende Pflichtfelder aus. Da sich
              die Schulung an gewerbliche Teilnehmer richtet, verwende bitte Deine geschäftliche E-Mail-Adresse. Im
              Anschluss erhältst Du Deinen Nachweis als PDF per E-Mail.
            </Typography>
            {isLoading && (
              <FormLoadingOverlay>
                <CircularProgress size={60} sx={{ color: 'primary.main' }} />
              </FormLoadingOverlay>
            )}
            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
            >
              {({ errors, touched, isValid, dirty, values, handleChange, handleBlur }) => (
                <Form>
                  {/* Honeypot field: hidden from real users; bots may fill it. */}
                  <Box
                    aria-hidden="true"
                    sx={{ position: 'absolute', left: '-5000px', width: '1px', height: '1px', overflow: 'hidden' }}
                  >
                    <label htmlFor="website">Dieses Feld bitte leer lassen</label>
                    <input
                      id="website"
                      type="text"
                      name="website"
                      tabIndex={-1}
                      autoComplete="off"
                      value={values.website}
                      onChange={handleChange}
                    />
                  </Box>
                  <Field
                    as={TextField}
                    fullWidth
                    margin="normal"
                    name="firstName"
                    label="Vorname"
                    error={touched.firstName && Boolean(errors.firstName)}
                    helperText={touched.firstName && errors.firstName}
                    sx={{ mb: 2 }}
                    disabled={isLoading}
                  />
                  <Field
                    as={TextField}
                    fullWidth
                    margin="normal"
                    name="lastName"
                    label="Nachname"
                    error={touched.lastName && Boolean(errors.lastName)}
                    helperText={touched.lastName && errors.lastName}
                    sx={{ mb: 2 }}
                    disabled={isLoading}
                  />
                  <Field
                    as={TextField}
                    fullWidth
                    margin="normal"
                    name="company"
                    label="Firmenname"
                    error={touched.company && Boolean(errors.company)}
                    helperText={touched.company && errors.company}
                    sx={{ mb: 2 }}
                    disabled={isLoading}
                  />
                  <TextField
                    fullWidth
                    margin="normal"
                    name="trainingDate"
                    label="Schulungstag"
                    type="date"
                    value={values.trainingDate}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.trainingDate && Boolean(errors.trainingDate)}
                    helperText={touched.trainingDate && errors.trainingDate}
                    slotProps={{ htmlInput: { max: today }, inputLabel: { shrink: true } }}
                    sx={{ mb: 2 }}
                    disabled={isLoading}
                  />
                  <Field
                    as={TextField}
                    fullWidth
                    margin="normal"
                    name="email"
                    label="Geschäftsmail"
                    type="email"
                    error={touched.email && Boolean(errors.email)}
                    helperText={touched.email && errors.email}
                    sx={{ mb: 2 }}
                    disabled={isLoading}
                  />
                  <PrivacyConsentSection
                    label="Ja, ich stimme zu und möchte den Teilnahme-Nachweis erhalten"
                    isLoading={isLoading}
                  />
                  {errorMessage && (
                    <Typography color="error" variant="body2" sx={{ mt: 2 }}>
                      {errorMessage}
                    </Typography>
                  )}
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                    <LanscoButton
                      type="submit"
                      disabled={!(isValid && dirty) || isLoading}
                      sx={{ minWidth: 180 }}
                    >
                      {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Nachweis anfordern'}
                    </LanscoButton>
                  </Box>
                </Form>
              )}
            </Formik>
          </>
        ) : (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography variant="h4" component="h1" gutterBottom sx={{ color: 'primary.main', fontWeight: 300 }}>
              Vielen Dank!
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Deine Angaben wurden erfolgreich übermittelt.
            </Typography>
            <Typography variant="body1">
              Du erhältst deinen Teilnahme-Nachweis in Kürze als PDF via E-Mail.
            </Typography>
          </Box>
        )}
      </FormPaper>
      <ErrorSnackbar
        open={showErrorSnackbar}
        message={errorMessage}
        onClose={() => setShowErrorSnackbar(false)}
      />
    </>
  );
};